import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { Provider } from '@/features/iam/providers/Provider'
import {
  type ProviderStore,
  type UpdateProviderParams,
} from '@/features/iam/providers/ProviderStore'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import type { Id } from '@/infra/id'
import { deepEqual } from '@/utils/object-util'
import { func, validationContext } from 'validx'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ActivationLifecycle } from '@/infra/activation'
import { ignoreAbortError } from '@/utils/abort-util'

export class ProDetailsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  @observable accessor softwareOwned: string[] = []
  @observable accessor softwareExperience: string[] = []
  @observable accessor supplementalSoftware: string[] = []
  @observable accessor specialties: string[] = []
  @observable accessor stateRegion: string | undefined = undefined
  @observable accessor yearsExperience = 0

  private _provider: Provider = Provider.none

  readonly validation = validationContext(this, {
    yearsExperience: [
      func(
        ({ value }) =>
          (typeof value === 'number' && value >= 0 && value <= 99) ||
          'Years of experience must be between 0 and 99',
      ),
    ],
  })

  constructor(
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _providerStore: ProviderStore,
    private readonly _userPublicId: Id,
    public readonly editable: boolean,
  ) {}

  activate() {
    this._lifecycle.activate()
    void this.fetch(this._lifecycle.signal).catch(ignoreAbortError())
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Loads the provider. Takes the signal explicitly so a host can load the
   * details before showing them, without activating this view model.
   */
  fetch = task(async (signal?: AbortSignal) => {
    this._provider =
      this._providerStore.primeProvider({
        userPublicId: this._userPublicId,
      }) ?? Provider.none
    await this._providerStore.fetchByPublicId(this._userPublicId, signal)
    this._initializeFromProvider()
  })

  @computed
  get hasChanged() {
    return !deepEqual(this._collectFromProvider(), this._collectForUpdate())
  }

  @computed
  get canSave() {
    return this.editable && !this.save.pending && this.hasChanged
  }

  @action.bound
  setSoftwareOwned(value: string[]) {
    this.softwareOwned = value ?? []
  }

  @action.bound
  setSoftwareExperience(value: string[]) {
    this.softwareExperience = value ?? []
  }

  @action.bound
  setSupplementalSoftware(value: string[]) {
    this.supplementalSoftware = value ?? []
  }

  @action.bound
  setSpecialties(value: string[]) {
    this.specialties = value ?? []
  }

  @action.bound
  setStateRegion(value: string | undefined) {
    this.stateRegion = value
  }

  @action.bound
  setYearsExperience(value: number | null) {
    this.yearsExperience = value ?? 0
  }

  save = task.resolved(async (): Promise<boolean> => {
    if (!this.hasChanged) {
      return true
    }

    if (!this.validation.reset().validate().isValid) {
      return false
    }

    try {
      await this._providerStore
        .updateProvider(this._userPublicId, this._collectForUpdate())
        .catch(this._createAlertErrorHandler('Saving pro details failed'))
    } catch {
      return false
    }
    this._initializeFromProvider()
    this._newAlert()
      .withState('success')
      .saying('Success!', 'Pro details have been updated.')
      .autoDismiss()
      .show()
    return true
  })

  private _collectForUpdate(): UpdateProviderParams {
    return {
      softwareOwned: this.softwareOwned,
      softwareExperience: this.softwareExperience,
      supplementalSoftwareExperience: this.supplementalSoftware,
      specialties: this.specialties,
      stateRegion: this.stateRegion ?? '',
      yearsExperience: this.yearsExperience,
    }
  }

  private _collectFromProvider(): UpdateProviderParams {
    return {
      softwareOwned: this._provider.softwareOwned,
      softwareExperience: this._provider.softwareExperience,
      supplementalSoftwareExperience:
        this._provider.supplementalSoftwareExperience,
      specialties: this._provider.specialties,
      stateRegion: this._provider.stateRegion,
      yearsExperience: this._provider.yearsExperience,
    }
  }

  @action.bound
  private _initializeFromProvider() {
    this.softwareOwned = [...this._provider.softwareOwned]
    this.softwareExperience = [...this._provider.softwareExperience]
    this.supplementalSoftware = [
      ...this._provider.supplementalSoftwareExperience,
    ]
    this.specialties = [...this._provider.specialties]
    this.stateRegion = this._provider.stateRegion || undefined
    this.yearsExperience = this._provider.yearsExperience
  }
}
